/**
 * `<ProDocLibrary>` — the **Retrieval** capability's package surface
 * (ADR-0010): the register (FR-15), the folder tree that browses it (FR-38),
 * the selection and its bulk bar (FR-16), and the detail drawer.
 *
 * UI_SYSTEM §13: a register gets everything — the column registry, the
 * arranger, the toolbar, the pager and an Excel export. **The service filters,
 * sorts and pages** (D18), because an organisation's store is ten thousand
 * documents; every control is state the host may mirror into its URL (`query`
 * / `onQueryChange`, and `libraryQueryToParams` for the address), so a filtered
 * view is a link and a saved view is that link with a name (D32).
 *
 * What it asks: a search over the title, file name, type, reference, notes,
 * custom fields and tag names; filters by workspace, **folder** (with or
 * without its subfolders), document type, **tag** (any of), review state,
 * owner app, **uploader**, **document date**, **upload date** and status;
 * sort by any sortable column. Each filter's choices carry the facet's count
 * with every other filter applied. Rows arrive with identifiers masked (D19);
 * the detail reads them in full.
 *
 * How it browses: the **folder tree** — every workspace the reader sees, its
 * folders one level at a time, a sibling app's folders grouped under the app,
 * and from W6 **Shared with me** (`view=shared`, the folders others shared as
 * its roots) — and **by app** (`owner_app`), with a breadcrumb over the rows.
 * The rows are a **table** or a **grid**. W6 also lets the archive authority
 * see **expired** tombstones and the documents the retention job **flagged**
 * (FR-26).
 *
 * W22 adds **how far each document has been read** (FR-55, FR-57, D52): a
 * *Reading* column in FR-55's five words and its filter (`?reading=` — the home
 * screen's tiles open the register with it); and **which files need a
 * password** (FR-58, D56): the *Password* column — *Not required*, *Locked*,
 * *Wrong password — try again*, *Unlocked* — with a box and *Unlock* on the row
 * for a person who may ask a parse, and the *Needs a password* filter
 * (`?password=locked`). Both columns are the service's answer, never a guess;
 * the date filters move to FR-54's presets in India time (D55). W30 (FR-67,
 * D103): the column gains **Saved** — the password kept sealed, used for the
 * reader, never shown — and the row's box **saves** the password
 * (`document:can_update`) instead of asking a parse; saved once, then final.
 * *Needs a password* keeps its meaning, the service's: encrypted, nothing
 * saved, no reading.
 *
 * W23 adds **which documents hold personal data** (FR-51, D69): the *Personal
 * data* column — the most sensitive kind the current version holds, *also
 * contains …* behind an ⓘ, or *None found*, *Not scanned*, *Checking* — and its
 * filter (`?personal_data=` a kind, `none`, `not_scanned` or `pending`). Kinds
 * only: no value, masked or not, is in a row.
 *
 * W25 (FR-60, D76) moves the folders **into the host's left menu**:
 * `browse="menu"` drops the register's own left panel — the tree and the *By
 * app* tab, which the *Owner app* filter's *Every app* already does — so the table
 * spans the width, and the host's menu (`useProDocFolderMenu`) chooses the
 * place through the address. A library opened with `ownerApp` never had the
 * panel, and is unchanged either way.
 *
 * W29 (FR-66, D90 – D94) puts the register **at a glance**: one row above the
 * table — the search, the *Uploaded* period, a **Filters** button saying how
 * many are on, the table / grid switch, then *Reset*, *Columns* and *Export* —
 * and no count in it; every filter in the **Filters panel** (the kit's
 * `Popover`, in the order of the columns they belong to, plus *Uploaded by*),
 * applying at once as before; what is on said once as **chips** between the
 * row and the table, each clearing its own filter; and the count only at the
 * foot, with the rows per page and the pager. Every query key and every prop is
 * unchanged — a saved link opens as it did.
 *
 * W29b (D96), on the kit's W29K (fiveox #447): **a funnel beside each filtered
 * column's name** (`ColumnDef.filter`, drawn by `DataTable`) — `options` for the
 * one-value filters and the tags, `custom` for FR-54's periods, the status with
 * its retention switch and the password switch, so each keeps the control it
 * has in the panel. One registry (`filterRegistry`) is the funnels, the chips
 * (the kit's `ActiveFilters`, given the whole of it, so a filter on a hidden
 * column is still said) and *Filters · n*; each value is read from the
 * `LibraryQuery` and written back to it (`filterValuesOf`, `filterChanges`), so
 * the address still carries it and the service still filters, sorts and pages.
 * The row is the kit's `TableToolbar` without its count, the foot its
 * `TableFooter`. The Filters panel stays — for the grid, a phone, a hidden
 * column and *Uploaded by*, which has no column.
 *
 * The selection column is still composed here (plan §7, path b): `DataTable`'s
 * `leading` column of the kit's `Checkbox`.
 */
import {
  ActiveFilters,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ColumnsPanel,
  DataTable,
  Dialog,
  EmptyState,
  MultiSelectFilter,
  NoMatches,
  Popover,
  SearchInput,
  Select,
  Skeleton,
  SkeletonRegion,
  Switch,
  TableFooter,
  TableToolbar,
  Tabs,
  describeColumnFilter,
  formatDate,
  formatDateTime,
  useColumnLayout,
  useToast,
  type ColumnDef,
  type ColumnFilter,
  type ColumnFilterOption,
  type ColumnFilterValue,
} from '@fiveox/ui'
import { LayoutGrid, Rows3, SlidersHorizontal } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { PASSWORD_WORDS, READING_STATES, READING_WORDS, readingReasonText, type ReadingState } from './extraction-model'
import {
  DOCUMENT,
  DOCUMENT_SORTS,
  FOLDER,
  MAX_SELECTION,
  REVIEW_STATES,
  documentTypeLabel,
  formatBytes,
  mayShare,
  passwordSavedOf,
  type DocumentQuery,
  type DocumentSort,
  type DocumentStatus,
  type Folder,
  type Page,
  type ProDocClient,
  type ProDocDocument,
  type ReviewState,
  type Tag,
} from './prodoc-client'
import { FileMark, ReadingBadge, ReviewBadge, StatusBadge, mediaLabel, plural, reviewLabel, statusLabel } from './bits'
import { BulkBar, type BulkReport } from './bulk-bar'
import { ProDocDocumentDetail } from './document-detail'
import { FolderDialogs, folderDoneText } from './folder-dialogs'
import { FolderBreadcrumb, FolderTree, useFolderList, type FolderAction, type FolderPlace } from './folder-tree'
import { errorText, uploaderLabel, useLoad, useProDocAccess, useProDocClient, useWorkspaces, type HostProps } from './hooks'
import { PeriodSelect } from './period-select'
import { PERIOD_PRESET_LABELS, parsePeriodPreset, periodRange, type IstPeriodPreset, type LegacyPeriodPreset, type PeriodValue } from './periods'
import { PERSONAL_DATA_FILTERS, PERSONAL_DATA_FILTER_WORDS, personalDataText, type PersonalDataFilter } from './personal-data'
import { PersonalDataSummary } from './personal-data-panel'
import { ShareDialog, type ShareTarget, type SharingHostProps } from './share-dialog'
import { SplitMark } from './split'
import { TagBadge, TagChips, useTags } from './tag-picker'
import { PasswordStatus, type PasswordSaveOutcome } from './unlock'

/* ------------------------------------------------------------- the query */

/**
 * A date filter: any time, one of FR-54's presets in India time (`periods.ts`,
 * D55 — weeks from Monday, the Indian financial year's quarters and years), or
 * a custom range. The kit's old names (`last7`, `last_month`, `this_fy`,
 * `last_fy`) are still read, so a saved address keeps filtering.
 */
export type DatePreset = 'any' | IstPeriodPreset | LegacyPeriodPreset

export interface DateFilter extends PeriodValue {
  preset: DatePreset
  /** `YYYY-MM-DD`, for `custom` only. */
  from: string
  to: string
}

export const ANY_TIME: DateFilter = { preset: 'any', from: '', to: '' }

/** The register's whole question, as state the host may keep in its URL. */
export interface LibraryQuery {
  q: string
  /** A workspace id, or `null` for every workspace. */
  workspace: string | null
  /** A folder of that workspace, or `null` for all of it. */
  folder: string | null
  /** With a folder: every folder below it too. */
  include_subfolders: boolean
  /** Tag ids — a document carrying **any** of them. */
  tags: string[]
  /** A document-type code (FR-5), or `null` for every type. */
  document_type: string | null
  owner_app: string | null
  /** A user id — whose upload. */
  uploaded_by: string | null
  document_date: DateFilter
  uploaded: DateFilter
  /** Archived and expired rows answer only a holder of `can_delete` on `document` (FR-24, FR-26). */
  status: DocumentStatus
  /** W6: only what members' shares put in the reader's sight (FR-29) — the folder, if any, is a shared one. */
  shared: boolean
  /** W6: only live documents past their `retain_until`, flagged by the daily job (FR-26). */
  retention: 'flagged' | null
  /** A review state (FR-20), or `null` for every state. `needs_review` is what waits for a reviewer. */
  review_state: ReviewState | null
  /**
   * W22 (FR-57, D52): the current version's reading in one of FR-55's states,
   * or `null` / absent for any. Optional, so a host's own query still reads.
   */
  reading?: ReadingState | null
  /** W22 (FR-58): `locked` — *Needs a password*: encrypted, and no reading of the current version yet. */
  password?: 'locked' | null
  /**
   * W23 (FR-51): the current version holds this kind of personal data — or
   * `none` was found, it was `not_scanned`, or it is `pending`. Optional, as W22's are.
   */
  personal_data?: PersonalDataFilter | null
  sort: DocumentSort
  dir: 'asc' | 'desc'
  view: 'table' | 'grid'
  page: number
  page_size: number
}

export const DEFAULT_QUERY: LibraryQuery = {
  q: '',
  workspace: null,
  folder: null,
  include_subfolders: false,
  tags: [],
  document_type: null,
  owner_app: null,
  uploaded_by: null,
  document_date: ANY_TIME,
  uploaded: ANY_TIME,
  status: 'stored',
  shared: false,
  retention: null,
  review_state: null,
  sort: 'uploaded_at',
  dir: 'desc',
  view: 'table',
  page: 1,
  page_size: 25,
}

export const PAGE_SIZES = [10, 25, 50, 100]
/** Bumped when the default layout changes, so a saved layout does not hide a new column. */
const LAYOUT_KEY = 'prodoc.library.columns.v5'
const STATUSES: DocumentStatus[] = ['stored', 'archived', 'expired']
/** The *Status* filter's words — its choices, and its chip. */
const STATUS_WORDS: Record<DocumentStatus, string> = { stored: 'Stored', archived: 'Archived', expired: 'Expired — file removed' }
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** The IST days a filter means as of `now` — `null` for any time, and for a custom range the picker would refuse. */
export function rangeOf(filter: DateFilter, now: Date | string = new Date()): { from: string; to: string } | null {
  return periodRange(filter, now)
}

/** The register's question as the service's parameters (plan §5.2), dates resolved as of `now` — an instant, or an IST day. */
export function apiQueryOf(query: LibraryQuery, scope: { ownerApp?: string; sourceRef?: string } = {}, now: Date | string = new Date()): DocumentQuery {
  const docDate = rangeOf(query.document_date, now)
  const uploaded = rangeOf(query.uploaded, now)
  return {
    q: query.q || undefined,
    workspace: query.workspace ?? undefined,
    folder: query.folder ?? undefined,
    include_subfolders: query.folder ? query.include_subfolders : undefined,
    tag: query.tags.length ? query.tags : undefined,
    document_type: query.document_type ?? undefined,
    owner_app: scope.ownerApp ?? query.owner_app ?? undefined,
    uploaded_by: query.uploaded_by ?? undefined,
    document_date_from: docDate?.from || undefined,
    document_date_to: docDate?.to || undefined,
    uploaded_from: uploaded?.from || undefined,
    uploaded_to: uploaded?.to || undefined,
    status: query.status,
    view: query.shared ? 'shared' : undefined,
    retention: query.retention ?? undefined,
    review_state: query.review_state ?? undefined,
    reading: query.reading ?? undefined,
    password: query.password ?? undefined,
    personal_data: query.personal_data ?? undefined,
    sort: query.sort,
    dir: query.dir,
    source_ref: scope.sourceRef,
    limit: query.page_size,
    offset: (query.page - 1) * query.page_size,
  }
}

function dateFrom(p: URLSearchParams, key: string, fromKey: string, toKey: string): DateFilter {
  // Today's names, or the kit's old ones read as today's (`this_fy` is `this_year`).
  const preset = parsePeriodPreset(p.get(key))
  if (preset === 'custom') {
    const from = p.get(fromKey) ?? ''
    const to = p.get(toKey) ?? ''
    return { preset, from: ISO_DATE.test(from) ? from : '', to: ISO_DATE.test(to) ? to : '' }
  }
  return preset ? { preset, from: '', to: '' } : ANY_TIME
}

function dateTo(p: URLSearchParams, filter: DateFilter, key: string, fromKey: string, toKey: string) {
  if (filter.preset === 'any') return
  p.set(key, filter.preset)
  if (filter.preset === 'custom') {
    if (filter.from) p.set(fromKey, filter.from)
    if (filter.to) p.set(toKey, filter.to)
  }
}

/**
 * The query from an address: `?q=&workspace=&folder=&sub=1&tag=…&type=&owner_app=&by=&doc_date=&doc_from=&doc_to=
 * &uploaded=&uploaded_from=&uploaded_to=&status=&shared=1&retention=flagged&review=&reading=&password=locked&personal_data=&sort=&dir=&view=grid&page=&size=`.
 * Anything the register does not know is ignored, so an old or hand-made link opens somewhere true.
 */
export function libraryQueryFromParams(p: URLSearchParams): LibraryQuery {
  const status = p.get('status') as DocumentStatus | null
  const review = p.get('review') as ReviewState | null
  const reading = p.get('reading') as ReadingState | null
  const personalData = p.get('personal_data') as PersonalDataFilter | null
  const sort = p.get('sort') as DocumentSort | null
  const size = Number(p.get('size'))
  // W1's `?workspace=none` is retired (D9): an old link opens every workspace.
  const workspace = p.get('workspace') && p.get('workspace') !== 'none' ? p.get('workspace') : null
  const folder = workspace ? p.get('folder') || null : null
  return {
    q: p.get('q') ?? '',
    workspace,
    folder,
    include_subfolders: Boolean(folder) && p.get('sub') === '1',
    tags: [...new Set(p.getAll('tag').filter(Boolean))],
    document_type: p.get('type') || null,
    owner_app: p.get('owner_app') || null,
    uploaded_by: p.get('by') || null,
    document_date: dateFrom(p, 'doc_date', 'doc_from', 'doc_to'),
    uploaded: dateFrom(p, 'uploaded', 'uploaded_from', 'uploaded_to'),
    status: status && STATUSES.includes(status) ? status : DEFAULT_QUERY.status,
    shared: p.get('shared') === '1',
    retention: p.get('retention') === 'flagged' ? 'flagged' : null,
    review_state: review && REVIEW_STATES.includes(review) ? review : null,
    // W22: present only when the address asks, so a query built before them reads the same.
    ...(reading && READING_STATES.includes(reading) ? { reading } : {}),
    ...(p.get('password') === 'locked' ? { password: 'locked' as const } : {}),
    // W23: likewise, only when asked — and only a choice the filter offers.
    ...(personalData && PERSONAL_DATA_FILTERS.includes(personalData) ? { personal_data: personalData } : {}),
    sort: sort && DOCUMENT_SORTS.includes(sort) ? sort : DEFAULT_QUERY.sort,
    dir: p.get('dir') === 'asc' ? 'asc' : p.get('dir') === 'desc' ? 'desc' : sort && DOCUMENT_SORTS.includes(sort) ? defaultDir(sort) : DEFAULT_QUERY.dir,
    view: p.get('view') === 'grid' ? 'grid' : 'table',
    page: Math.max(1, Number(p.get('page') ?? 1) || 1),
    page_size: PAGE_SIZES.includes(size) ? size : DEFAULT_QUERY.page_size,
  }
}

/** Only what differs from the default goes in the address, so a plain `/documents` stays plain. */
export function libraryQueryToParams(q: LibraryQuery): URLSearchParams {
  const p = new URLSearchParams()
  if (q.q) p.set('q', q.q)
  if (q.workspace) p.set('workspace', q.workspace)
  if (q.workspace && q.folder) {
    p.set('folder', q.folder)
    if (q.include_subfolders) p.set('sub', '1')
  }
  for (const tag of q.tags) p.append('tag', tag)
  if (q.document_type) p.set('type', q.document_type)
  if (q.owner_app) p.set('owner_app', q.owner_app)
  if (q.uploaded_by) p.set('by', q.uploaded_by)
  dateTo(p, q.document_date, 'doc_date', 'doc_from', 'doc_to')
  dateTo(p, q.uploaded, 'uploaded', 'uploaded_from', 'uploaded_to')
  if (q.status !== DEFAULT_QUERY.status) p.set('status', q.status)
  if (q.shared) p.set('shared', '1')
  if (q.retention) p.set('retention', q.retention)
  if (q.review_state) p.set('review', q.review_state)
  if (q.reading) p.set('reading', q.reading)
  if (q.password) p.set('password', q.password)
  if (q.personal_data) p.set('personal_data', q.personal_data)
  if (q.sort !== DEFAULT_QUERY.sort || q.dir !== DEFAULT_QUERY.dir) {
    p.set('sort', q.sort)
    p.set('dir', q.dir)
  }
  if (q.view !== DEFAULT_QUERY.view) p.set('view', q.view)
  if (q.page > 1) p.set('page', String(q.page))
  if (q.page_size !== DEFAULT_QUERY.page_size) p.set('size', String(q.page_size))
  return p
}

/** The direction a column sorts in first: names and types A→Z, dates and sizes newest and largest first. */
function defaultDir(sort: DocumentSort): 'asc' | 'desc' {
  return sort === 'title' || sort === 'document_type' || sort === 'review_requested_at' ? 'asc' : 'desc'
}

/** Whether anything narrows the register — the difference between "nothing yet" and "nothing matches". */
export function isFiltered(q: LibraryQuery, ownerAppFixed = false): boolean {
  return Boolean(
    q.q ||
      q.workspace ||
      q.folder ||
      q.tags.length ||
      q.document_type ||
      (!ownerAppFixed && q.owner_app) ||
      q.uploaded_by ||
      q.document_date.preset !== 'any' ||
      q.uploaded.preset !== 'any' ||
      q.status !== 'stored' ||
      q.shared ||
      q.retention ||
      q.review_state ||
      q.reading ||
      q.password ||
      q.personal_data,
  )
}

/** Keys whose change keeps the selection: paging, sorting, the view. Anything else is another question. */
const SAME_QUESTION: (keyof LibraryQuery)[] = ['page', 'page_size', 'sort', 'dir', 'view']

/** The question itself — the query without its paging, sorting and view — as one comparable string, whatever its keys' order; a `null` filter is the same as an absent one. */
function questionOf(q: LibraryQuery): string {
  const rest: Record<string, unknown> = { ...q }
  for (const key of SAME_QUESTION) delete rest[key]
  return JSON.stringify(
    Object.keys(rest)
      .filter((key) => rest[key] != null)
      .sort()
      .map((key) => [key, rest[key]]),
  )
}

/**
 * What choosing a place changes in the register's question — the same wherever
 * it is chosen (the register's panel, its breadcrumb, a host's menu): a
 * workspace, or a folder of it, out of *Shared with me*, keeping *include
 * subfolders* only while a folder is chosen. Every other filter stays.
 */
export function placeChanges(query: LibraryQuery, place: FolderPlace): Partial<LibraryQuery> {
  return { shared: false, workspace: place.workspace, folder: place.folder, include_subfolders: place.folder ? query.include_subfolders : false }
}

/** W6: *Shared with me* — everything, or one shared folder with everything under it (a folder share is recursive, §9-18). */
export function sharedChanges(folder: Pick<Folder, 'id' | 'workspace_id'> | null): Partial<LibraryQuery> {
  return { shared: true, workspace: folder?.workspace_id ?? null, folder: folder?.id ?? null, include_subfolders: Boolean(folder) }
}

/* ------------------------------------------------------------- the props */

export interface ProDocLibraryProps extends HostProps, SharingHostProps {
  /** Narrow the register to one app's records — a sibling embedding its own documents. */
  ownerApp?: string
  /** …and to one record of that app. */
  sourceRef?: string
  /** Controlled: the host keeps the query (in its URL). Omit for internal state. */
  query?: LibraryQuery
  onQueryChange?: (next: LibraryQuery) => void
  /** Open a document the host's way — a route. Without it, the row opens the drawer here. */
  onOpen?: (document: ProDocDocument) => void
  /** Offered when there is nothing yet — the host's "Upload" button. */
  emptyAction?: ReactNode
  /** Where the ZIP's ticket is opened — the host's window by default. */
  navigate?: (url: string) => void
  /** W22: how soon an unlock's parse was first asked about again. Unused since W30 — the row's box saves the password, asking no parse; kept so a host's prop still types. */
  jobPollMs?: number
  /**
   * W25 (FR-60): where the place is chosen. `panel` (the default) — the
   * register's own left panel, *Folders* and *By app*; `menu` — the host's left
   * menu holds the tree (`useProDocFolderMenu`), so the register has no panel
   * and the table spans the width. With `ownerApp` there is no panel either way.
   */
  browse?: 'panel' | 'menu'
  /**
   * W25: bumped by a host whose own folder dialogs changed something (the
   * menu's — `useProDocFolderMenu`'s `revision`): the register reads its
   * folders' names, its breadcrumb and its rows again.
   */
  folderRevision?: number
  /**
   * W25: the register changed what a folder holds — a bulk move or archive —
   * so a host showing its own tree reads it again (`useProDocFolderMenu`'s `refresh`).
   */
  onFolderChange?: () => void
  className?: string
}

/** The row plus what the screen names it by — resolved once per page, not per cell. */
type Row = ProDocDocument & { workspace: string | null; uploader: string; typeLabel: string; folderName: string | null; tagList: Tag[]; tagNames: string }

/** What a row's *Password* cell needs of the screen (W30, D103): the client, whether this person may save a password, and the re-read after a save. */
interface RowContext {
  client: ProDocClient | null
  mayUpdate: boolean
  onSaved: (outcome: PasswordSaveOutcome) => void
}

const RowCtx = createContext<RowContext>({ client: null, mayUpdate: false, onSaved: () => {} })

/* ------------------------------------------------------------ the screen */

export function ProDocLibrary(props: ProDocLibraryProps) {
  const { ownerApp, sourceRef, onOpen, emptyAction, className, userId, onFolderChange } = props
  // W25: the register's own panel only where the host's menu does not browse — and never for a sibling's records.
  const showPanel = !ownerApp && (props.browse ?? 'panel') === 'panel'
  const hostRevision = props.folderRevision ?? 0
  const client = useProDocClient(props)
  const access = useProDocAccess(client)
  const { toast } = useToast()
  const workspaces = useWorkspaces(client)
  const options = useLoad(() => client.documents.options(), [client])
  const tags = useTags(client, access.can, access.loading)
  const types = options.data?.document_types ?? []
  const app = options.data?.app ?? 'prodoc'
  const navigate = props.navigate ?? ((url: string) => window.location.assign(url))

  const [internal, setInternal] = useState<LibraryQuery>(DEFAULT_QUERY)
  const query = props.query ?? internal
  const [selected, setSelected] = useState<Map<string, ProDocDocument>>(new Map())
  // A new question asked from outside — a place chosen in the host's menu, back or forward — starts a new
  // selection too, as one asked here does. Adjusted during render, so the old selection never paints under it.
  const question = questionOf(query)
  const [seenQuestion, setSeenQuestion] = useState(question)
  if (seenQuestion !== question) {
    setSeenQuestion(question)
    if (selected.size > 0) setSelected(new Map())
  }
  const setQuery = (changes: Partial<LibraryQuery>) => {
    // Any change but paging goes back to page 1; a new question starts a new selection.
    const next = { ...query, ...changes, page: 'page' in changes ? (changes.page ?? 1) : 1 }
    if (Object.keys(changes).some((k) => !SAME_QUESTION.includes(k as keyof LibraryQuery))) setSelected(new Map())
    if (props.onQueryChange) props.onQueryChange(next)
    else setInternal(next)
  }

  const [result, setResult] = useState<Page<ProDocDocument> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState(query.q)
  const [arranging, setArranging] = useState(false)
  const [open, setOpen] = useState<ProDocDocument | null>(null)
  const [reloads, setReloads] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [panel, setPanel] = useState<'folders' | 'apps'>('folders')
  const [folderAction, setFolderAction] = useState<FolderAction | null>(null)
  const [folderRevision, setFolderRevision] = useState(0)
  const [report, setReport] = useState<BulkReport | null>(null)
  const [sharing, setSharing] = useState<ShareTarget | null>(null)
  const layout = useColumnLayout(LAYOUT_KEY, COLUMNS, { leadingWidth: SELECT_WIDTH })
  const latest = useRef(0)
  const folders = useFolderList(client, access.can(FOLDER, 'can_view') ? query.workspace : null)

  // W25: the host's own folder dialogs changed something — the names, the breadcrumb and the rows are read again.
  useEffect(() => {
    if (hostRevision === 0) return
    void folders.reload()
    setReloads((n) => n + 1)
    // Only a new revision re-reads; `folders.reload` follows the workspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostRevision])

  const requestKey = JSON.stringify({ ...query, view: undefined, ownerApp, sourceRef, reloads })
  useEffect(() => setDraft(query.q), [query.q])

  useEffect(() => {
    let cancelled = false
    const ticket = ++latest.current
    setLoading(true)
    client.documents
      .list(apiQueryOf(query, { ownerApp, sourceRef }))
      .then((page) => {
        // A slow answer to an older question must not overwrite a newer one.
        if (cancelled || ticket !== latest.current) return
        setResult(page)
        setError('')
      })
      .catch((e) => {
        if (!cancelled && ticket === latest.current) setError(errorText(e))
      })
      .finally(() => {
        if (!cancelled && ticket === latest.current) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // The query object is the whole question; `requestKey` stands for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, requestKey])

  const visible = access.answer?.visible_workspace_ids
  const seen = useMemo(() => workspaces.list.filter((w) => w.status !== 'archived' && (visible == null || visible.includes(w.id))), [workspaces.list, visible])
  const folderNames = useMemo(() => new Map((folders.data ?? []).map((f: Folder) => [f.id, f.name])), [folders.data])
  const tagsById = useMemo(() => new Map(tags.list.map((t) => [t.id, t])), [tags.list])
  const people = result?.people ?? {}
  const personName = useCallback((id: string) => (userId && id === userId ? 'you' : (people[id] ?? 'another member')), [people, userId])

  const rows: Row[] = useMemo(
    () =>
      (result?.items ?? []).map((d) => ({
        ...d,
        workspace: workspaces.nameOf(d.workspace_id),
        uploader: d.uploaded_by ? (people[d.uploaded_by] && d.uploaded_by !== userId ? people[d.uploaded_by]! : uploaderLabel(d.uploaded_by, userId)) : 'an app',
        typeLabel: documentTypeLabel(d.document_type, types),
        folderName: !d.folder_id ? null : (folderNames.get(d.folder_id) ?? 'In a folder'),
        ...tagsOf(d.tag_ids, tagsById),
      })),
    [result, workspaces.nameOf, userId, types, folderNames, tagsById, people],
  )
  const total = result?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / query.page_size))
  const facets = result?.facets
  const filtered = isFiltered(query, Boolean(ownerApp))
  const nothingYet = !loading && !error && result !== null && total === 0 && !filtered
  const canArchive = access.can(DOCUMENT, 'can_delete')
  const canFolders = access.can(FOLDER, 'can_view')
  const rowContext: RowContext = {
    client,
    // W30 (FR-67): saving a password is `document:can_update`; the service checks it again.
    mayUpdate: access.can(DOCUMENT, 'can_update'),
    // The column is the service's answer again after any save — right, wrong or refused.
    onSaved: () => setReloads((n) => n + 1),
  }

  const reset = () => {
    setQuery({ ...DEFAULT_QUERY, page_size: query.page_size, view: query.view })
    layout.resetLayout()
  }

  const exportRows = async () => {
    setExporting(true)
    try {
      // On demand: `xlsx` stays off the entry chunk (D21).
      const { exportRegister } = await import('./sheet-export')
      exportRegister(rows, layout.visible, 'prodoc-documents')
    } catch (e) {
      toast({ message: errorText(e, 'The export could not be written.'), tone: 'error' })
    } finally {
      setExporting(false)
    }
  }

  const openRow = (row: ProDocDocument) => {
    if (onOpen) onOpen(row)
    else setOpen(row)
  }

  const toggleRow = (row: ProDocDocument, on: boolean) =>
    setSelected((s) => {
      const next = new Map(s)
      if (on) next.set(row.id, row)
      else next.delete(row.id)
      return next
    })
  const pageIds = rows.map((r) => r.id)
  const pageSelected = pageIds.filter((id) => selected.has(id)).length
  const togglePage = (on: boolean) =>
    setSelected((s) => {
      const next = new Map(s)
      for (const row of rows) {
        if (on && next.size < MAX_SELECTION) next.set(row.id, row)
        if (!on) next.delete(row.id)
      }
      return next
    })

  const place: FolderPlace = { workspace: query.workspace, folder: query.folder }
  const choosePlace = (p: FolderPlace) => setQuery(placeChanges(query, p))
  /** W6: *Shared with me* — everything, or one shared folder with everything under it (a folder share is recursive, §9-18). */
  const chooseShared = (f: Folder | null) => setQuery(sharedChanges(f))
  // `GET /folders?shared=true` is behind `folder:can_view`, as every folder read is (app/api/folders.py).
  const sharedFolders = useLoad(() => (access.can(FOLDER, 'can_view') ? client.folders.shared() : Promise.resolve([] as Folder[])), [client, access.answer])

  if (!access.loading && !access.can(DOCUMENT, 'can_view')) {
    return (
      <EmptyState
        title="You can’t see documents here"
        description="Your ProDoc role in this organisation does not include viewing documents. An administrator can change it in the FiveOX console."
        className={className}
      />
    )
  }

  // Each filter's choices, with the facet's count beside them. A value the
  // facets name but the lists do not (an archived workspace, a disabled type)
  // still gets a row — a filter must reach every row.
  const counted = (facet: Record<string, number> | undefined, value: string) => {
    const n = facet?.[value]
    return n === undefined ? '' : ` (${n})`
  }
  const workspaceIds = [...new Set([...seen.map((w) => w.id), ...Object.keys(facets?.workspace ?? {})])]
  const defaultId = workspaces.list.find((w) => w.is_default)?.id ?? null
  const typeCodes = [...new Set([...types.map((t) => t.code), ...Object.keys(facets?.document_type ?? {})])]
  if (query.document_type && !typeCodes.includes(query.document_type)) typeCodes.push(query.document_type)
  const reviewStates = options.data?.review_states?.length ? options.data.review_states : REVIEW_STATES
  const ownerApps = Object.keys(facets?.owner_app ?? {}).sort()
  if (query.owner_app && !ownerApps.includes(query.owner_app)) ownerApps.push(query.owner_app)
  const uploaders = Object.keys(facets?.uploaded_by ?? {})
  if (query.uploaded_by && !uploaders.includes(query.uploaded_by)) uploaders.push(query.uploaded_by)
  const tagOptions = tags.list.map((t) => ({ value: t.id, label: t.name, sublabel: facets?.tag?.[t.id] !== undefined ? `${facets.tag[t.id]} here` : undefined }))

  const workspaceName = (id: string | null) => (id ? workspaces.nameOf(id) : null)
  // The names the panel's choices show — and, without their counts, what a chip says.
  const workspaceLabel = (id: string) => `${workspaces.nameOf(id) ?? id}${id === defaultId ? ' (default)' : ''}`
  const uploaderName = (id: string) => (personName(id) === 'you' ? 'You' : personName(id) === 'another member' ? `A member (…${id.slice(-4)})` : personName(id))

  // W29b (D91, D96): every filter once, in D91's order — the funnels, the chips and *Filters · n* read this list.
  // The search, the folder and the shared root are not in it: the search box and the breadcrumb say them. A
  // choice keeps its facet's count (`sublabel`); a chip says the choice's label without it.
  const here = (facet: Record<string, number> | undefined, value: string) => (facet?.[value] === undefined ? undefined : `${facet[value]} here`)
  const tagChoices: ColumnFilterOption[] = [
    ...tagOptions,
    // A tag the reader cannot see or that was deleted since still narrows the rows, so its chip still says one.
    ...query.tags.filter((id) => !tagsById.has(id)).map((id) => ({ value: id, label: `A tag (…${id.slice(-4)})` })),
  ]
  const uploaderIds = userId && !uploaders.includes(userId) ? [userId, ...uploaders] : uploaders
  const registry: RegistryEntry[] = filterRegistry({
    ownerApp,
    // D91: Status and *Past their retention date* only for the archive authority (FR-24); Tags for a reader of tags.
    canArchive,
    canTags: tags.visible,
    type: typeCodes.map((code) => ({ value: code, label: documentTypeLabel(code, types), sublabel: here(facets?.document_type, code) })),
    workspace: workspaceIds.map((wid) => ({ value: wid, label: workspaceLabel(wid), sublabel: here(facets?.workspace, wid) })),
    tags: tagChoices,
    ownerApps: ownerApps.map((owner) => ({ value: owner, label: owner, sublabel: here(facets?.owner_app, owner) })),
    review: reviewStates.map((state) => ({ value: state, label: reviewLabel(state), sublabel: here(facets?.review_state, state) })),
    uploadedBy: uploaderIds.map((id) => ({ value: id, label: uploaderName(id), sublabel: here(facets?.uploaded_by, id) })),
  })
  const filterValues = filterValuesOf(query, Boolean(ownerApp))
  const filtersOn = registry.filter((entry) => describeColumnFilter(entry.filter, filterValues[entry.key]) !== null)
  const setFilter = (key: string, next: ColumnFilterValue | null) => setQuery(filterChanges(key, next))
  /** *Clear filters*: every chip at once — not the search, not the place, not the layout (*Reset* does those). */
  const clearFilters = () => setQuery(Object.assign({}, ...filtersOn.map((entry) => filterChanges(entry.key, null))) as Partial<LibraryQuery>)
  // The funnels go on the columns shown; a hidden column's filter is still a chip (`ActiveFilters` takes the registry).
  const funnels = new Map(registry.filter((entry) => entry.funnel).map((entry) => [entry.key, entry.filter]))
  const tableColumns = layout.visible.map((column) => {
    const filter = funnels.get(column.key)
    return filter ? { ...column, filter } : column
  })

  return (
    <div className={className}>
      {error && (
        <Alert tone="error" className="mb-3">
          {error}{' '}
          <Button variant="link" onClick={() => setReloads((n) => n + 1)}>
            Try again
          </Button>
        </Alert>
      )}

      {nothingYet ? (
        <EmptyState
          title="No documents yet"
          description="Upload PDFs, scans, images, spreadsheets and Word documents. ProDoc stores each one in this organisation’s private store, filed in a workspace and a folder under the type you give it, and finds it again by title, type, tag, folder, workspace or owner app."
          action={emptyAction}
        />
      ) : (
        <div className={showPanel ? 'grid items-start gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]' : undefined}>
          {showPanel && (
            <Card padding="sm" className="space-y-3 lg:sticky lg:top-4">
              <Tabs
                idBase="prodoc-browse"
                tabs={[...(canFolders ? [{ id: 'folders', label: 'Folders' }] : []), { id: 'apps', label: 'By app' }]}
                active={canFolders ? panel : 'apps'}
                onChange={(id) => setPanel(id as 'folders' | 'apps')}
              />
              {canFolders && panel === 'folders' ? (
                <FolderTree
                  client={client}
                  workspaces={seen}
                  workspacesLoading={workspaces.loading}
                  workspacesError={workspaces.error}
                  onRetryWorkspaces={() => void workspaces.reload()}
                  app={app}
                  selected={place}
                  onSelect={choosePlace}
                  can={access.can}
                  onAction={(a) => (a.kind === 'share' ? setSharing({ documents: [], folders: [{ id: a.folder.id, name: a.folder.name }] }) : setFolderAction(a))}
                  revision={folderRevision}
                  shared={{ folders: sharedFolders.data, loading: sharedFolders.loading && !sharedFolders.data, error: sharedFolders.error, active: query.shared, folder: query.shared ? query.folder : null, onSelect: chooseShared }}
                />
              ) : (
                <ByApp facets={facets?.owner_app} loading={loading && !result} selected={query.owner_app} app={app} onSelect={(owner) => setQuery({ owner_app: owner })} />
              )}
            </Card>
          )}

          <div className="min-w-0 space-y-3">
            {query.shared && (
              <p className="text-sm" aria-label="Breadcrumb">
                <span className={query.folder ? 'text-muted-foreground' : 'text-foreground font-medium'}>Shared with me</span>
                {query.folder && <span className="text-foreground font-medium"> › {(sharedFolders.data ?? []).find((f) => f.id === query.folder)?.name ?? 'A shared folder'}</span>}
              </p>
            )}

            {query.workspace && !query.shared && (
              <FolderBreadcrumb
                client={client}
                place={place}
                workspaceName={workspaceName(query.workspace)}
                onSelect={choosePlace}
                revision={folderRevision + hostRevision}
                trailing={
                  query.folder && (
                    <Switch
                      label="Include subfolders"
                      checked={query.include_subfolders}
                      onChange={(e) => setQuery({ include_subfolders: e.target.checked })}
                      className="ml-auto"
                    />
                  )
                }
              />
            )}

            {/* W29 (FR-66, D90): one row — the search and the upload period frame the register, every other
                filter is in its column's funnel and in the Filters panel, the tools sit at the right. W29b (D96): the
                kit's toolbar, given no count — it is at the foot (D94) — and no search of its own, because this one
                asks the service on Enter. The view switch is the last of its children: the toolbar has no place
                between its *Reset* and its *Columns*. */}
            <div data-register-row="">
              <TableToolbar
                onReset={reset}
                canReset={filtered || !layout.isDefault}
                onColumns={query.view === 'table' ? () => setArranging(true) : undefined}
                columnsChanged={!layout.isDefault}
                // Nothing to write while nothing matches: the toolbar disables Export only on a count it is not given.
                onExport={rows.length > 0 ? () => void exportRows() : undefined}
                exporting={exporting}
              >
                <form
                  role="search"
                  className="w-full min-w-0 sm:w-64"
                  onSubmit={(e) => {
                    e.preventDefault()
                    setQuery({ q: draft.trim() })
                  }}
                >
                  <SearchInput
                    value={draft}
                    onChange={(v) => {
                      setDraft(v)
                      // The × clears the search outright, not just the box.
                      if (!v && query.q) setQuery({ q: '' })
                    }}
                    placeholder="Search title, file, tag, notes, reference…"
                    label="Search documents"
                    className="sm:w-full"
                  />
                </form>
                <DateRangeFilter label="Uploaded" value={query.uploaded} onChange={(v) => setQuery({ uploaded: v })} />
                {/* D92: every filter in one panel — outside the table's scroll box, so never clipped; pinned between the gutters on a phone. */}
                <Popover
                  label="Filters"
                  className="max-sm:fixed max-sm:inset-x-4 max-sm:top-24 max-sm:mt-0 max-sm:max-h-[calc(100dvh-8rem)] max-sm:w-auto max-sm:overflow-y-auto sm:w-[34rem]"
                  trigger={({ open: _open, ...triggerProps }) => (
                    <Button {...triggerProps} variant="outline" size="sm" className={filtersOn.length > 0 ? 'border-primary/40 text-primary' : undefined}>
                      <SlidersHorizontal size={15} strokeWidth={1.75} aria-hidden />
                      {filtersOn.length > 0 ? `Filters · ${filtersOn.length}` : 'Filters'}
                    </Button>
                  )}
                >
                  {(close) => (
                    <div className="space-y-4">
                      {/* D91's order — the columns' order, then *Uploaded by*, which has no column. Each applies at once, as before. */}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <FilterField label="Type">
                          <Select size="sm" aria-label="Document type" value={query.document_type ?? ''} onChange={(e) => setQuery({ document_type: e.target.value || null })}>
                            <option value="">Every type</option>
                            {typeCodes.map((code) => (
                              <option key={code} value={code}>
                                {documentTypeLabel(code, types)}
                                {counted(facets?.document_type, code)}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                        <FilterField label="Document date">
                          <DateRangeFilter label="Document date" value={query.document_date} onChange={(v) => setQuery({ document_date: v })} prefixed={false} />
                        </FilterField>
                        <FilterField label="Workspace">
                          <Select size="sm" aria-label="Workspace" value={query.workspace ?? ''} onChange={(e) => setQuery({ workspace: e.target.value || null, folder: null, include_subfolders: false })}>
                            <option value="">Every workspace</option>
                            {workspaceIds.map((wid) => (
                              <option key={wid} value={wid}>
                                {workspaceLabel(wid)}
                                {counted(facets?.workspace, wid)}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                        {tags.visible && (
                          <FilterField label="Tags">
                            <TagFilter options={tagOptions} selected={query.tags} onApply={(next) => setQuery({ tags: next })} />
                          </FilterField>
                        )}
                        {!ownerApp && (
                          <FilterField label="Owner app">
                            <Select size="sm" aria-label="Owner app" value={query.owner_app ?? ''} onChange={(e) => setQuery({ owner_app: e.target.value || null })}>
                              <option value="">Every app</option>
                              {ownerApps.map((owner) => (
                                <option key={owner} value={owner}>
                                  {owner}
                                  {counted(facets?.owner_app, owner)}
                                </option>
                              ))}
                            </Select>
                          </FilterField>
                        )}
                        {canArchive && (
                          // Archived documents answer only an archiver (FR-24); nobody else is offered the choice.
                          <FilterField label="Status">
                            <Select size="sm" aria-label="Status" value={query.status} onChange={(e) => setQuery({ status: e.target.value as DocumentStatus, retention: e.target.value === 'stored' ? query.retention : null })}>
                              {STATUSES.map((status) => (
                                <option key={status} value={status}>
                                  {STATUS_WORDS[status]}
                                </option>
                              ))}
                            </Select>
                            {query.status === 'stored' && (
                              // FR-26: live documents past their date are flagged, never removed — someone decides.
                              <Switch label="Past their retention date" checked={query.retention === 'flagged'} onChange={(e) => setQuery({ retention: e.target.checked ? 'flagged' : null })} className="mt-2" />
                            )}
                          </FilterField>
                        )}
                        <FilterField label="Review">
                          <Select size="sm" aria-label="Review" value={query.review_state ?? ''} onChange={(e) => setQuery({ review_state: (e.target.value || null) as ReviewState | null })}>
                            <option value="">Any review state</option>
                            {reviewStates.map((state) => (
                              <option key={state} value={state}>
                                {reviewLabel(state)}
                                {counted(facets?.review_state, state)}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                        <FilterField label="Reading">
                          <Select size="sm" aria-label="Reading" value={query.reading ?? ''} onChange={(e) => setQuery({ reading: (e.target.value || null) as ReadingState | null })}>
                            <option value="">Any reading</option>
                            {READING_STATES.map((state) => (
                              <option key={state} value={state}>
                                {READING_WORDS[state]}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                        {/* FR-58: the encrypted documents with no reading yet. */}
                        <FilterField label="Password">
                          <Switch label="Needs a password" checked={query.password === 'locked'} onChange={(e) => setQuery({ password: e.target.checked ? 'locked' : null })} />
                        </FilterField>
                        {/* W23 (FR-51): by the kind of personal data the current version holds, or by its scan's state. */}
                        <FilterField label="Personal data">
                          <Select size="sm" aria-label="Personal data" value={query.personal_data ?? ''} onChange={(e) => setQuery({ personal_data: (e.target.value || null) as PersonalDataFilter | null })}>
                            <option value="">Any personal data</option>
                            {PERSONAL_DATA_FILTERS.map((choice) => (
                              <option key={choice} value={choice}>
                                {PERSONAL_DATA_FILTER_WORDS[choice]}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                        {/* The row's period again, so the panel holds every filter. */}
                        <FilterField label="Uploaded">
                          <DateRangeFilter label="Uploaded" value={query.uploaded} onChange={(v) => setQuery({ uploaded: v })} prefixed={false} />
                        </FilterField>
                        <FilterField label="Uploaded by">
                          <Select size="sm" aria-label="Uploaded by" value={query.uploaded_by ?? ''} onChange={(e) => setQuery({ uploaded_by: e.target.value || null })}>
                            <option value="">Anyone</option>
                            {userId && !uploaders.includes(userId) && <option value={userId}>You</option>}
                            {uploaders.map((id) => (
                              <option key={id} value={id}>
                                {uploaderName(id)}
                                {counted(facets?.uploaded_by, id)}
                              </option>
                            ))}
                          </Select>
                        </FilterField>
                      </div>
                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={close}>
                          Done
                        </Button>
                      </div>
                    </div>
                  )}
                </Popover>
                <ViewToggle view={query.view} onChange={(view) => setQuery({ view })} />
              </TableToolbar>
            </div>

            {/* D93: what is on, said once — each chip clears its own filter; drawn only while one is on. The whole
                registry, not the columns shown: a filter on a hidden column still narrows the rows. */}
            <ActiveFilters columns={registry} filters={filterValues} onClear={(key) => setFilter(key, null)} onClearAll={clearFilters} />

            {report && <BulkReportAlert report={report} onDismiss={() => setReport(null)} />}

            <BulkBar
              client={client}
              selected={[...selected.values()]}
              can={access.can}
              tags={tags}
              workspaces={seen}
              currentWorkspace={query.workspace}
              app={app}
              onClear={() => setSelected(new Map())}
              navigate={navigate}
              onShare={mayShare(access.can) ? () => setSharing({ documents: [...selected.values()].map((d) => ({ id: d.id, title: d.title })), folders: [] }) : undefined}
              onDone={(next, changedIds) => {
                setReport(next)
                // What changed leaves the selection; what was refused stays, to try again.
                setSelected((s) => {
                  const kept = new Map(s)
                  for (const id of changedIds) kept.delete(id)
                  return kept
                })
                setReloads((n) => n + 1)
                setFolderRevision((n) => n + 1)
                // A host's own tree counts what each folder holds, too.
                onFolderChange?.()
              }}
            />

            {loading && !result ? (
              <SkeletonRegion label="Loading documents" className="space-y-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </SkeletonRegion>
            ) : rows.length === 0 && !error ? (
              <NoMatches
                query={query.q || describe(query, { workspace: workspaceName(query.workspace), folder: query.folder ? (folderNames.get(query.folder) ?? 'this folder') : null })}
                action={
                  <Button variant="outline" onClick={reset}>
                    Clear the filters
                  </Button>
                }
              >
                {noMatchesText(query)}
              </NoMatches>
            ) : query.view === 'grid' ? (
              <Grid
                rows={rows}
                tags={tags.list}
                selected={selected}
                onToggle={toggleRow}
                onOpen={openRow}
              />
            ) : (
              <RowCtx.Provider value={rowContext}>
                <DataTable
                  columns={tableColumns}
                  rows={rows}
                  rowKey={(r) => r.id}
                  offsets={layout.offsets}
                  pinned={layout.pinned}
                  // W29b (D96): a funnel beside each filtered column's name — the query's values, written back to it.
                  filters={filterValues}
                  onFilter={setFilter}
                  loading={loading && !result}
                  maxHeight="65vh"
                  onRowClick={openRow}
                  sortField={FIELD_OF[query.sort] ?? null}
                  sortDir={query.dir}
                  onSort={(field) => {
                    const sort = SORT_OF[field as string]
                    if (!sort) return
                    setQuery({ sort, dir: sort === query.sort ? (query.dir === 'asc' ? 'desc' : 'asc') : defaultDir(sort) })
                  }}
                  leading={{
                    width: SELECT_WIDTH,
                    header: (
                      <PageCheckbox
                        all={pageIds.length > 0 && pageSelected === pageIds.length}
                        some={pageSelected > 0}
                        label={pageSelected === pageIds.length && pageIds.length > 0 ? 'Clear this page’s selection' : 'Select this page'}
                        onChange={togglePage}
                      />
                    ),
                    cell: (row) => (
                      // A tick is not an open: the click stays in the cell.
                      <span onClick={(e) => e.stopPropagation()} className="flex">
                        <Checkbox
                          aria-label={`Select ${row.title}`}
                          checked={selected.has(row.id)}
                          disabled={!selected.has(row.id) && selected.size >= MAX_SELECTION}
                          onChange={(e) => toggleRow(row, e.target.checked)}
                        />
                      </span>
                    ),
                  }}
                />
              </RowCtx.Provider>
            )}

            {rows.length > 0 && (
              // D94: the count, the pages and the rows per page under the rows they count — the table's and the grid's.
              <TableFooter
                total={total}
                page={Math.min(query.page, totalPages)}
                totalPages={totalPages}
                setPage={(n) => setQuery({ page: n })}
                pageSize={query.page_size}
                setPageSize={(n) => setQuery({ page_size: n })}
                pageSizes={PAGE_SIZES}
                noun="document"
                className="mt-0"
              />
            )}
          </div>
        </div>
      )}

      {/* The panel's tree asks for these; under `browse="menu"` the host's menu mounts its own, once. */}
      {showPanel && (
        <FolderDialogs
          client={client}
          action={folderAction}
          workspaceName={(id) => workspaces.nameOf(id)}
          onClose={() => setFolderAction(null)}
          onDone={(folder, kind) => {
            const was = folderAction
            setFolderAction(null)
            setFolderRevision((n) => n + 1)
            void folders.reload()
            onFolderChange?.()
            toast(folderDoneText(folder, kind))
            if (kind === 'create') choosePlace({ workspace: folder.workspace_id, folder: folder.id })
            // An archived folder the register was standing in: step up to its parent.
            if (kind === 'archive' && was?.kind === 'archive' && query.folder === was.folder.id) choosePlace({ workspace: was.folder.workspace_id, folder: was.folder.parent_id })
          }}
        />
      )}

      <ShareDialog
        client={client}
        open={sharing !== null}
        target={sharing ?? { documents: [], folders: [] }}
        can={access.can}
        userId={userId}
        listMembers={props.listMembers}
        requestStepUp={props.requestStepUp}
        shareLinkBase={props.shareLinkBase}
        onClose={() => setSharing(null)}
      />

      <Dialog open={arranging} onClose={() => setArranging(false)} title="Arrange columns" width="xl">
        <ColumnsPanel columns={COLUMNS} layout={layout.layout} onChange={layout.setLayout} onReset={layout.resetLayout} isDefault={layout.isDefault} />
      </Dialog>

      {/* The detail drawer — the kit's dialog at its widest, for a host with no route of its own. */}
      <Dialog open={open !== null} onClose={() => setOpen(null)} title={open?.title ?? 'Document'} width="xl">
        {open && (
          <ProDocDocumentDetail
            apiUrl={props.apiUrl}
            fetcher={props.fetcher}
            orgId={props.orgId}
            userId={userId}
            documentId={open.id}
            listMembers={props.listMembers}
            requestStepUp={props.requestStepUp}
            shareLinkBase={props.shareLinkBase}
            compact
            onArchived={() => {
              setOpen(null)
              setReloads((n) => n + 1)
            }}
            onChanged={() => setReloads((n) => n + 1)}
          />
        )}
      </Dialog>
    </div>
  )
}

/** The no-matches sentence for the view the reader is in. */
function noMatchesText(q: LibraryQuery): string {
  if (q.password === 'locked' && !q.q) return 'No document here needs a password — every encrypted one has its password saved or has been read, or none was uploaded.'
  if (q.shared && !q.q) return q.folder ? 'Nothing in this shared folder matches these filters.' : 'Nothing has been shared with you here yet — or what was has expired or been revoked.'
  if (q.status === 'expired') return 'No expired document matches. An expired document keeps its record and history here after its file is removed.'
  if (q.retention === 'flagged') return 'No document is past its retention date.'
  if (q.status === 'archived') return 'No archived document matches these filters.'
  if (q.folder && !q.q) return 'Nothing is filed in this folder with these filters. Its subfolders may hold more — include them, or browse into them.'
  return 'No document you can see matches these filters.'
}

/** "Finance › Returns, Invoice, approved" — what is being asked, for the no-matches line. */
function describe(q: LibraryQuery, names: { workspace: string | null; folder: string | null }): string {
  const parts = [
    names.folder ?? names.workspace,
    q.document_type?.replace(/_/g, ' '),
    q.review_state ? reviewLabel(q.review_state).toLowerCase() : null,
    q.reading ? READING_WORDS[q.reading].toLowerCase() : null,
    q.password ? 'needs a password' : null,
    q.personal_data ? `personal data: ${PERSONAL_DATA_FILTER_WORDS[q.personal_data]}` : null,
    q.tags.length ? plural(q.tags.length, 'tag') : null,
  ]
  return parts.filter(Boolean).join(', ') || 'these filters'
}

/* ------------------------------------------------------------- filters */

/** Tags, any of — the kit's staged multi-select: nothing is asked until Apply. */
function TagFilter({ options, selected, onApply }: { options: { value: string; label: string; sublabel?: string }[]; selected: string[]; onApply: (next: string[]) => void }) {
  return <MultiSelectFilter label="Tags" options={options} selected={selected} onApply={onApply} searchPlaceholder="Find a tag…" />
}

/**
 * A date filter (FR-54, D55): *any time* — the library's default — then the
 * presets in India time, and a from–to pair for a custom range, which refuses
 * a start after its end (`PeriodSelect`).
 */
export function DateRangeFilter({ label, value, onChange, prefixed = true }: { label: string; value: DateFilter; onChange: (next: DateFilter) => void; prefixed?: boolean }) {
  return <PeriodSelect label={label} value={value} onChange={(next) => onChange(next as DateFilter)} anyLabel="Any time" prefixed={prefixed} />
}

/* ------------------------------------------------- the funnels (W29b, D96) */

/** One filter of D91: the key of its column (or `uploaded_by`, which has none), its name, the kit's `filter`, and whether its column carries the funnel. */
export interface RegistryEntry {
  key: string
  label: string
  filter: ColumnFilter
  funnel: boolean
}

const single = (options: ColumnFilterOption[]): ColumnFilter => ({ kind: 'options', single: true, options })

/** An FR-54 period in a funnel — the same picker as the row's, applying at once. */
const periodFilter = (label: string): ColumnFilter => ({
  kind: 'custom',
  width: 320,
  describe: (value) => {
    const period = periodOf(value)
    return period.preset === 'any' ? null : periodText(period)
  },
  render: ({ value, onChange, close }) => (
    <FunnelPanel close={close}>
      <DateRangeFilter label={label} value={periodOf(value)} onChange={(next) => onChange(periodValue(next) ?? null)} prefixed={false} />
    </FunnelPanel>
  ),
})

/** The status, and *Past their retention date* beside it — as the Filters panel has them (FR-24, FR-26). */
const statusFilter: ColumnFilter = {
  kind: 'custom',
  describe: (value) => {
    const { status, retention } = statusOf(value)
    const words = [...(status !== 'stored' ? [STATUS_WORDS[status]] : []), ...(retention ? ['Past their retention date'] : [])]
    return words.length ? words.join(', ') : null
  },
  render: ({ value, onChange, close }) => {
    const { status, retention } = statusOf(value)
    return (
      <FunnelPanel close={close}>
        <Select size="sm" aria-label="Status" value={status} onChange={(e) => onChange(statusValue(e.target.value as DocumentStatus, e.target.value === 'stored' && retention))}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_WORDS[s]}
            </option>
          ))}
        </Select>
        {status === 'stored' && <Switch label="Past their retention date" checked={retention} onChange={(e) => onChange(statusValue('stored', e.target.checked))} />}
      </FunnelPanel>
    )
  },
}

/** FR-58's *Needs a password*: encrypted, nothing saved, no reading of the current version yet. */
const passwordFilter: ColumnFilter = {
  kind: 'custom',
  describe: (value) => (value.values?.includes('locked') ? 'Needs a password' : null),
  render: ({ value, onChange, close }) => (
    <FunnelPanel close={close}>
      <Switch label="Needs a password" checked={Boolean(value?.values?.includes('locked'))} onChange={(e) => onChange(e.target.checked ? { values: ['locked'] } : null)} />
    </FunnelPanel>
  ),
}

/**
 * D91's filters in its order — the columns' order, then *Uploaded by*, which has
 * no column and so no funnel. *Owner app* is not one in a sibling's library
 * (its rows are its app's); Status and Tags carry a funnel only for who may use
 * them, but stay chips for anyone who arrived with them on (a link), because
 * they still narrow the rows.
 */
export function filterRegistry(inputs: {
  ownerApp: string | undefined
  canArchive: boolean
  canTags: boolean
  type: ColumnFilterOption[]
  workspace: ColumnFilterOption[]
  tags: ColumnFilterOption[]
  ownerApps: ColumnFilterOption[]
  review: ColumnFilterOption[]
  uploadedBy: ColumnFilterOption[]
}): RegistryEntry[] {
  return [
    { key: 'document_type', label: 'Type', filter: single(inputs.type), funnel: true },
    { key: 'document_date', label: 'Document date', filter: periodFilter('Document date'), funnel: true },
    { key: 'workspace', label: 'Workspace', filter: single(inputs.workspace), funnel: true },
    { key: 'tags', label: 'Tags', filter: { kind: 'options', options: inputs.tags }, funnel: inputs.canTags },
    ...(inputs.ownerApp ? [] : [{ key: 'owner_app', label: 'Owner app', filter: single(inputs.ownerApps), funnel: true }]),
    { key: 'status', label: 'Status', filter: statusFilter, funnel: inputs.canArchive },
    { key: 'review_state', label: 'Review', filter: single(inputs.review), funnel: true },
    { key: 'reading', label: 'Reading', filter: single(READING_STATES.map((state) => ({ value: state, label: READING_WORDS[state] }))), funnel: true },
    { key: 'password', label: 'Password', filter: passwordFilter, funnel: true },
    { key: 'personal_data', label: 'Personal data', filter: single(PERSONAL_DATA_FILTERS.map((choice) => ({ value: choice, label: PERSONAL_DATA_FILTER_WORDS[choice] }))), funnel: true },
    { key: 'created_at', label: 'Uploaded', filter: periodFilter('Uploaded'), funnel: true },
    { key: 'uploaded_by', label: 'Uploaded by', filter: single(inputs.uploadedBy), funnel: false },
  ]
}

const one = (value: string | null | undefined): ColumnFilterValue | undefined => (value ? { values: [value] } : undefined)

/** A period as a filter's value: the preset, and a custom range's days — `undefined` for any time. */
function periodValue(filter: DateFilter): ColumnFilterValue | undefined {
  if (filter.preset === 'any') return undefined
  return filter.preset === 'custom' ? { values: ['custom'], from: filter.from || undefined, to: filter.to || undefined } : { values: [filter.preset] }
}

/** …and back: a preset the register does not know is any time, as an address's is. */
function periodOf(value: ColumnFilterValue | null | undefined): DateFilter {
  const preset = value?.values?.[0]
  if (preset === 'custom') return { preset, from: value?.from ?? '', to: value?.to ?? '' }
  const known = parsePeriodPreset(preset)
  return known ? { preset: known, from: '', to: '' } : ANY_TIME
}

/** The *Status* funnel's value: a status other than *Stored*, and `flagged` for *Past their retention date*. */
function statusValue(status: DocumentStatus, retention: boolean): ColumnFilterValue | null {
  const values = [...(status !== 'stored' ? [status] : []), ...(retention ? ['flagged'] : [])]
  return values.length ? { values } : null
}

function statusOf(value: ColumnFilterValue | null | undefined): { status: DocumentStatus; retention: boolean } {
  const values = value?.values ?? []
  const status = (values.find((v) => STATUSES.includes(v as DocumentStatus)) as DocumentStatus | undefined) ?? 'stored'
  return { status, retention: status === 'stored' && values.includes('flagged') }
}

/** Each filter's value, read from the register's question — the funnels' and the chips' one source. */
export function filterValuesOf(query: LibraryQuery, ownerAppFixed = false): Record<string, ColumnFilterValue | undefined> {
  return {
    document_type: one(query.document_type),
    document_date: periodValue(query.document_date),
    // A workspace with a folder chosen is the place, said by the breadcrumb; alone, it is a filter (W29a).
    workspace: query.workspace && !query.folder && !query.shared ? { values: [query.workspace] } : undefined,
    tags: query.tags.length ? { values: query.tags } : undefined,
    owner_app: ownerAppFixed ? undefined : one(query.owner_app),
    status: statusValue(query.status, query.retention === 'flagged') ?? undefined,
    review_state: one(query.review_state),
    reading: one(query.reading),
    password: query.password === 'locked' ? { values: ['locked'] } : undefined,
    personal_data: one(query.personal_data),
    created_at: periodValue(query.uploaded),
    uploaded_by: one(query.uploaded_by),
  }
}

/** …and a filter's new value (`null` clears it) written back to the question — the address carries it, the service filters. */
export function filterChanges(key: string, next: ColumnFilterValue | null): Partial<LibraryQuery> {
  const first = next?.values?.[0] ?? null
  switch (key) {
    case 'document_type':
      return { document_type: first }
    case 'document_date':
      return { document_date: periodOf(next) }
    case 'workspace':
      // As the panel's choice does: a workspace is all of it, no folder.
      return { workspace: first, folder: null, include_subfolders: false }
    case 'tags':
      return { tags: next?.values ?? [] }
    case 'owner_app':
      return { owner_app: first }
    case 'status': {
      const { status, retention } = statusOf(next)
      return { status, retention: retention ? 'flagged' : null }
    }
    case 'review_state':
      return { review_state: first && REVIEW_STATES.includes(first as ReviewState) ? (first as ReviewState) : null }
    case 'reading':
      return { reading: first && READING_STATES.includes(first as ReadingState) ? (first as ReadingState) : null }
    case 'password':
      return { password: next?.values?.includes('locked') ? 'locked' : null }
    case 'personal_data':
      return { personal_data: first && PERSONAL_DATA_FILTERS.includes(first as PersonalDataFilter) ? (first as PersonalDataFilter) : null }
    case 'created_at':
      return { uploaded: periodOf(next) }
    case 'uploaded_by':
      return { uploaded_by: first }
    default:
      return {}
  }
}

/** A `custom` funnel's inside: the screen's own control, applying at once, and *Done* — the Filters panel's close. */
function FunnelPanel({ close, children }: { close: () => void; children: ReactNode }) {
  return (
    <div className="space-y-3 p-3">
      {children}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={close}>
          Done
        </Button>
      </div>
    </div>
  )
}

/** A period as a chip says it (W29, D93): the preset's own words, or a custom range's days. */
function periodText(filter: DateFilter): string {
  if (filter.preset === 'custom') {
    if (filter.from && filter.to) return `${formatDate(filter.from)} – ${formatDate(filter.to)}`
    return filter.from ? `from ${formatDate(filter.from)}` : filter.to ? `up to ${formatDate(filter.to)}` : PERIOD_PRESET_LABELS.custom
  }
  const preset = parsePeriodPreset(filter.preset)
  return preset ? PERIOD_PRESET_LABELS[preset] : PERIOD_PRESET_LABELS.custom
}

/** One filter in the Filters panel (W29, D92), named on screen as its column is. */
function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      {children}
    </div>
  )
}

/** The page's tick box: ticked when every row on the page is, mixed when some are (the kit's `Checkbox` takes no ref, so the box is reached through its wrapper). */
function PageCheckbox({ all, some, label, onChange }: { all: boolean; some: boolean; label: string; onChange: (on: boolean) => void }) {
  const wrap = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    const box = wrap.current?.querySelector('input')
    if (box) box.indeterminate = some && !all
  })
  return (
    <span ref={wrap} className="flex">
      <Checkbox aria-label={label} checked={all} onChange={(e) => onChange(e.target.checked)} />
    </span>
  )
}

function ViewToggle({ view, onChange }: { view: 'table' | 'grid'; onChange: (view: 'table' | 'grid') => void }) {
  return (
    <span className="flex items-center gap-1" role="group" aria-label="View">
      <Button variant={view === 'table' ? 'secondary' : 'ghost'} size="sm" aria-pressed={view === 'table'} aria-label="Table view" title="Table view" onClick={() => onChange('table')}>
        <Rows3 size={15} aria-hidden />
      </Button>
      <Button variant={view === 'grid' ? 'secondary' : 'ghost'} size="sm" aria-pressed={view === 'grid'} aria-label="Grid view" title="Grid view" onClick={() => onChange('grid')}>
        <LayoutGrid size={15} aria-hidden />
      </Button>
    </span>
  )
}

/** Browse by app (FR-15): each `owner_app` with its count, this app's own first. */
function ByApp({ facets, loading, selected, app, onSelect }: { facets: Record<string, number> | undefined; loading: boolean; selected: string | null; app: string; onSelect: (owner: string | null) => void }) {
  if (loading) {
    return (
      <SkeletonRegion label="Loading the apps" className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-24" />
      </SkeletonRegion>
    )
  }
  const apps = Object.entries(facets ?? {}).sort(([a], [b]) => (a === app ? -1 : b === app ? 1 : a.localeCompare(b)))
  if (apps.length === 0) return <p className="text-muted-foreground text-sm">No documents to group yet.</p>
  return (
    <ul className="space-y-0.5" aria-label="Documents by app">
      <li>
        <Button variant="ghost" size="sm" className={`w-full justify-start ${!selected ? 'bg-primary/10 text-primary' : ''}`} aria-pressed={!selected} onClick={() => onSelect(null)}>
          Every app
        </Button>
      </li>
      {apps.map(([owner, count]) => (
        <li key={owner}>
          <Button variant="ghost" size="sm" className={`w-full justify-between ${selected === owner ? 'bg-primary/10 text-primary' : ''}`} aria-pressed={selected === owner} onClick={() => onSelect(owner)}>
            <span className="truncate">{owner === app ? `${owner} (uploaded here)` : owner}</span>
            <span className="text-muted-foreground text-xs">{count}</span>
          </Button>
        </li>
      ))}
    </ul>
  )
}

/** What a bulk action did, said once: how many changed, and each one that did not with the service's reason. */
function BulkReportAlert({ report, onDismiss }: { report: BulkReport; onDismiss: () => void }) {
  const tone = report.refused.length === 0 ? 'success' : report.changed === 0 ? 'error' : 'warning'
  return (
    <Alert tone={tone}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {report.verb} {plural(report.changed, 'document')}
            {report.refused.length > 0 ? ` — ${plural(report.refused.length, 'was', 'were')} not:` : '.'}
          </p>
          {report.refused.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm" aria-label="Not changed">
              {report.refused.map((r) => (
                <li key={r.id}>
                  <span className="font-medium">{r.title}</span> — {r.message}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </Alert>
  )
}

/* ---------------------------------------------------------------- grid */

function Grid({
  rows,
  tags,
  selected,
  onToggle,
  onOpen,
}: {
  rows: Row[]
  tags: ReturnType<typeof useTags>['list']
  selected: Map<string, ProDocDocument>
  onToggle: (row: ProDocDocument, on: boolean) => void
  onOpen: (row: ProDocDocument) => void
}) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label="Documents">
      {rows.map((row) => (
        <li key={row.id}>
          <Card padding="sm" className={`h-full space-y-2 ${selected.has(row.id) ? 'border-primary/50' : ''}`}>
            <div className="flex items-start gap-3">
              <Checkbox
                aria-label={`Select ${row.title}`}
                checked={selected.has(row.id)}
                disabled={!selected.has(row.id) && selected.size >= MAX_SELECTION}
                onChange={(e) => onToggle(row, e.target.checked)}
              />
              <FileMark mediaType={row.media_type} />
              <div className="min-w-0 flex-1">
                <Button variant="link" className="h-auto max-w-full justify-start truncate px-0 text-left" onClick={() => onOpen(row)} title={row.title}>
                  {row.title}
                </Button>
                <p className="text-muted-foreground truncate text-xs">
                  {[row.typeLabel, row.workspace, row.folderName].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {row.status !== 'stored' && <StatusBadge status={row.status} size="sm" />}
              <ReviewBadge state={row.review_state} size="sm" />
              {/* W23 (FR-51): the card says it holds personal data, as the column does; quiet otherwise. */}
              {row.personal_data?.state === 'found' && <PersonalDataSummary data={row.personal_data} />}
              {row.owner_app !== 'prodoc' && <Badge size="sm">{row.owner_app}</Badge>}
              <SplitMark row={row} />
              <TagChips ids={row.tag_ids} tags={tags} empty="" />
            </div>
            <p className="text-muted-foreground text-xs">
              {formatBytes(row.size_bytes)} · {formatDate(row.created_at)} by {row.uploader}
            </p>
          </Card>
        </li>
      ))}
    </ul>
  )
}

/* ---------------------------------------------------------------- columns */

const SELECT_WIDTH = 44

/** A sortable column's row field → the service's sort key, and back. */
const SORT_OF: Record<string, DocumentSort> = {
  title: 'title',
  document_type: 'document_type',
  document_date: 'document_date',
  size_bytes: 'size',
  created_at: 'uploaded_at',
  review_state: 'review_requested_at',
}
const FIELD_OF = Object.fromEntries(Object.entries(SORT_OF).map(([field, sort]) => [sort, field])) as Partial<Record<DocumentSort, keyof Row>>

function NameCell({ row }: { row: Row }) {
  const second = [row.title !== row.file_name ? row.file_name : null, row.source_ref ? `${row.owner_app} · ${row.source_ref}` : null].filter(Boolean).join(' · ')
  return (
    <div className="flex min-w-0 items-start gap-3">
      <FileMark mediaType={row.media_type} size="sm" />
      <div className="min-w-0">
        <p className="text-foreground max-w-72 truncate font-medium" title={row.title}>
          {row.title}
        </p>
        <p className="text-muted-foreground flex max-w-72 items-center gap-1.5 truncate text-xs">
          {/* W37 (C8): a part's pages, a split original's mark — from the row itself. */}
          <SplitMark row={row} />
          <span className="truncate">{second || mediaLabel(row.media_type)}</span>
        </p>
      </div>
    </div>
  )
}

const muted = (text: string) => <span className="text-muted-foreground">{text}</span>

/** FR-58 on the row: the service's word, FR-67's *Saved*, and the box with *Save password* for a person who may save it. */
function PasswordCell({ row }: { row: Row }) {
  const ctx = useContext(RowCtx)
  if ((!row.password && !passwordSavedOf(row)) || !ctx.client) return muted('—')
  return <PasswordStatus client={ctx.client} doc={row} mayUpdate={ctx.mayUpdate} onSaved={ctx.onSaved} layout="row" />
}

/** FR-55's five words for the current version's reading, and why it failed beneath. */
function ReadingCell({ row }: { row: Row }) {
  if (!row.reading) return muted('—')
  const why = row.reading.state === 'failed' || row.reading.reason === 'app_review' ? readingReasonText(row.reading.reason) : ''
  return (
    <div>
      <ReadingBadge reading={row.reading} size="sm" />
      {why && <p className="text-muted-foreground mt-0.5 text-xs">{why}</p>}
    </div>
  )
}

const passwordExport = (row: Row) => (passwordSavedOf(row) ? PASSWORD_WORDS.saved : row.password ? { not_required: 'Not required', locked: 'Locked', wrong: 'Wrong password', unlocked: 'Unlocked', saved: PASSWORD_WORDS.saved }[row.password] : '')

/** A row's tags, named from the organisation's list — an id the list does not hold (a tag deleted since) is left out. */
function tagsOf(ids: string[] | undefined, byId: Map<string, Tag>): { tagList: Tag[]; tagNames: string } {
  const tagList = (ids ?? []).map((id) => byId.get(id)).filter((t): t is Tag => Boolean(t))
  return { tagList, tagNames: tagList.map((t) => t.name).join(', ') }
}

/** The tags as pills in their colours; more than three fold into "+N". */
function TagsCell({ row }: { row: Row }) {
  if (row.tagList.length === 0) return muted('—')
  return (
    <span className="flex max-w-56 flex-wrap gap-1" title={row.tagNames}>
      {row.tagList.slice(0, 3).map((t) => (
        <TagBadge key={t.id} tag={t} />
      ))}
      {row.tagList.length > 3 && <Badge size="sm">+{row.tagList.length - 3}</Badge>}
    </span>
  )
}

const COLUMNS: ColumnDef<Row>[] = [
  { key: 'title', label: 'Document', width: 300, required: true, sortField: 'title', render: (row) => <NameCell row={row} />, exportValue: (row) => row.title },
  { key: 'document_type', label: 'Type', width: 150, sortField: 'document_type', render: (row) => row.typeLabel, exportValue: (row) => row.typeLabel },
  {
    key: 'document_date',
    label: 'Document date',
    width: 140,
    sortField: 'document_date',
    render: (row) => (row.document_date ? formatDate(row.document_date) : muted('—')),
    exportValue: (row) => row.document_date ?? '',
  },
  { key: 'workspace', label: 'Workspace', width: 160, render: (row) => row.workspace ?? muted('—'), exportValue: (row) => row.workspace ?? '' },
  { key: 'folder', label: 'Folder', width: 160, render: (row) => row.folderName ?? muted('Top level'), exportValue: (row) => row.folderName ?? '' },
  { key: 'tags', label: 'Tags', width: 200, render: (row) => <TagsCell row={row} />, exportValue: (row) => row.tagNames },
  { key: 'owner_app', label: 'Owner app', width: 120, render: (row) => row.owner_app, exportValue: (row) => row.owner_app },
  { key: 'media_type', label: 'Kind', width: 130, render: (row) => mediaLabel(row.media_type), exportValue: (row) => mediaLabel(row.media_type) },
  {
    key: 'size_bytes',
    label: 'Size',
    width: 110,
    align: 'right',
    sortField: 'size_bytes',
    render: (row) => formatBytes(row.size_bytes),
    // The digits, not the string — a spreadsheet should be able to sum it.
    exportValue: (row) => row.size_bytes,
  },
  { key: 'status', label: 'Status', width: 120, render: (row) => <StatusBadge status={row.status} size="sm" />, exportValue: (row) => statusLabel(row.status) },
  {
    key: 'review_state',
    label: 'Review',
    width: 140,
    sortField: 'review_state',
    // `uploaded` asks nothing of a reviewer, so it is a dash, not a badge.
    render: (row) => (row.review_state && row.review_state !== 'uploaded' ? <ReviewBadge state={row.review_state} size="sm" /> : muted('—')),
    exportValue: (row) => reviewLabel(row.review_state),
  },
  // W22 (FR-55, D52): how far the current version has been read — the service's state.
  { key: 'reading', label: 'Reading', width: 150, render: (row) => <ReadingCell row={row} />, exportValue: (row) => (row.reading ? READING_WORDS[row.reading.state] : '') },
  // W22 (FR-58, D56): beside the status and the review, the password and its *Unlock*.
  { key: 'password', label: 'Password', width: 230, render: (row) => <PasswordCell row={row} />, exportValue: passwordExport },
  // W23 (FR-51, D69): the most sensitive kind the current version holds, the rest behind an ⓘ — kinds, never a value.
  {
    key: 'personal_data',
    label: 'Personal data',
    width: 170,
    render: (row) => <PersonalDataSummary data={row.personal_data} />,
    // The sheet names every kind found, not only the first.
    exportValue: (row) => personalDataText(row.personal_data, true),
  },
  {
    key: 'created_at',
    label: 'Uploaded',
    width: 180,
    sortField: 'created_at',
    render: (row) => (
      <div>
        <p className="text-foreground">{formatDate(row.created_at)}</p>
        <p className="text-muted-foreground text-xs">by {row.uploader}</p>
      </div>
    ),
    exportValue: (row) => formatDateTime(row.created_at),
  },
  {
    key: 'file_name',
    label: 'File name',
    width: 220,
    render: (row) => (
      <span className="block max-w-56 truncate" title={row.file_name}>
        {row.file_name}
      </span>
    ),
    exportValue: (row) => row.file_name,
  },
  {
    key: 'sha256',
    label: 'SHA-256',
    width: 200,
    render: (row) => (
      <span className="font-mono text-xs" title={row.sha256}>
        {row.sha256.slice(0, 16)}…
      </span>
    ),
    exportValue: (row) => row.sha256,
  },
]
